import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SocialShare from '../components/SocialShare';
import './ThankYou.css';

const ThankYou = () => {
  const location = useLocation();
  const bookingType = location.state?.bookingType;
  const isCommunityCenter = bookingType === 'community-center';

  return (
    <>
      <Helmet>
        <title>Thank You - Booking Confirmed | The Damtua Inn</title>
        <meta name="description" content="Thank you for choosing The Damtua Inn! Your booking has been confirmed. We look forward to welcoming you to Alikadam, Bandarban." />
        <meta name="robots" content="noindex, nofollow" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Thank You - Booking Confirmed | The Damtua Inn" />
        <meta property="og:description" content="Thank you for choosing The Damtua Inn! Your booking has been confirmed." />
        <meta property="og:type" content="website" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://damtuainn.com/thank-you" />
        
        {/* Google Analytics */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-H51BLHEXKM"></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-H51BLHEXKM');
          `}
        </script>
      </Helmet>

      <div className="thank-you-page">
        <div className="thank-you-container">
          <div className="thank-you-header">
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>Thank You!</h1>
            <h2>Your Booking Has Been Confirmed</h2>
          </div>

          <div className="thank-you-content">
            <div className="confirmation-details">
              {isCommunityCenter ? (
                <>
                  <div className="detail-card">
                    <h3>🏛️ Community Center Booking</h3>
                    <p>Your community center booking request has been submitted successfully. Our team will review your request and contact you within 24 hours to confirm availability and discuss event details.</p>
                  </div>

                  <div className="detail-card">
                    <h3>📞 Direct Contact</h3>
                    <p>For immediate assistance or to discuss your event requirements, please call us directly at +8801879917302.</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="detail-card">
                    <h3>📧 Confirmation Email</h3>
                    <p>We've sent a confirmation email to your registered email address with all the booking details.</p>
                  </div>

                  <div className="detail-card">
                    <h3>📱 Email Confirmation</h3>
                    <p>You should receive an email shortly with your booking confirmation and contact details.</p>
                  </div>
                </>
              )}
            </div>

            <div className="next-steps">
              <h3>What's Next?</h3>
              <div className="steps-list">
                {isCommunityCenter ? (
                  <>
                    <div className="step-item">
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <h4>Await Confirmation</h4>
                        <p>Our team will review your request and contact you within 24 hours to confirm availability and discuss your event requirements.</p>
                      </div>
                    </div>

                    <div className="step-item">
                      <div className="step-number">2</div>
                      <div className="step-content">
                        <h4>Event Planning</h4>
                        <p>Once confirmed, we'll work with you to plan the details of your event, including setup, catering options, and any special arrangements.</p>
                      </div>
                    </div>

                    <div className="step-item">
                      <div className="step-number">3</div>
                      <div className="step-content">
                        <h4>Enjoy Your Event</h4>
                        <p>We look forward to hosting your special event at our beautiful community center and making it memorable.</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="step-item">
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <h4>Prepare for Your Stay</h4>
                        <p>Pack your essentials and check our location details for easy navigation.</p>
                      </div>
                    </div>

                    <div className="step-item">
                      <div className="step-number">2</div>
                      <div className="step-content">
                        <h4>Contact Us</h4>
                        <p>Reach out to us 24 hours before your arrival for any special requests or changes.</p>
                      </div>
                    </div>

                    <div className="step-item">
                      <div className="step-number">3</div>
                      <div className="step-content">
                        <h4>Enjoy Your Stay</h4>
                        <p>We look forward to providing you with an exceptional experience at The Damtua Inn.</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="contact-info">
              <h3>Need Help?</h3>
              <div className="contact-grid">
                <div className="contact-item">
                  <div className="contact-icon">🏨</div>
                  <div className="contact-details">
                    <h4>Hotel Booking</h4>
                    <p>+8801748912127</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">🍽️</div>
                  <div className="contact-details">
                    <h4>Restaurant</h4>
                    <p>+8801883535200</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">📞</div>
                  <div className="contact-details">
                    <h4>General Enquiry</h4>
                    <p>+8801879917302</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">📧</div>
                  <div className="contact-details">
                    <h4>Email</h4>
                    <p>thedamtuainn@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="social-share-section">
              <SocialShare 
                url="https://damtuainn.com"
                title="The Damtua Inn - Best Hotel in Alikadam, Bandarban"
                description="Experience luxury accommodation, local cuisine, and stunning hill views at The Damtua Inn. Book your stay now!"
                image="https://damtuainn.com/og-image.jpg"
              />
            </div>

            <div className="action-buttons">
              <Link to="/" className="btn-primary">
                🏠 Back to Home
              </Link>
              {isCommunityCenter ? (
                <>
                  <Link to="/community-center" className="btn-secondary">
                    🏛️ Community Center
                  </Link>
                  <Link to="/restaurant" className="btn-secondary">
                    🍽️ Our Restaurant
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/rooms" className="btn-secondary">
                    🏨 View Rooms
                  </Link>
                  <Link to="/restaurant" className="btn-secondary">
                    🍽️ Our Restaurant
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ThankYou;
